import type { Expense, ExpenseCategory } from '../types';

export interface ExpenseBalance {
  total: number;
  byPayer: { dave: number; rich: number };
  owedBy: 'dave' | 'rich' | null;
  owedTo: 'dave' | 'rich' | null;
  owedAmount: number;
}

// Assumes an even 50/50 split between the two payers. Expenses paid from the
// shared/joint account are already understood to be split evenly (both
// contribute equally to that account) so they count toward the total but
// don't affect who-owes-whom.
export function computeBalance(expenses: Expense[]): ExpenseBalance {
  const byPayer = { dave: 0, rich: 0 };
  let total = 0;
  for (const e of expenses) {
    total += e.amount;
    if (e.paid_by === 'dave' || e.paid_by === 'rich') {
      byPayer[e.paid_by] += e.amount;
    }
  }
  const diff = byPayer.dave - byPayer.rich;
  const owedAmount = Math.abs(diff) / 2;

  if (Math.round(owedAmount * 100) === 0) {
    return { total, byPayer, owedBy: null, owedTo: null, owedAmount: 0 };
  }

  return diff > 0
    ? { total, byPayer, owedBy: 'rich', owedTo: 'dave', owedAmount }
    : { total, byPayer, owedBy: 'dave', owedTo: 'rich', owedAmount };
}

export interface CategoryYearRow {
  category: ExpenseCategory;
  byYear: Record<number, number>;
  total: number;
}

export interface CategoryYearMatrix {
  years: number[];
  rows: CategoryYearRow[];
  yearTotals: Record<number, number>;
  grandTotal: number;
}

// Fiscal year runs Sept 1 -> Aug 31, labeled by its ending calendar year
// (e.g. Sept 2025 - Aug 2026 is "FY2026") to match how this account's
// statements are actually cut.
function fiscalYear(dateISO: string): number {
  const d = new Date(dateISO + 'T00:00:00');
  const month = d.getMonth() + 1; // 1-12
  const year = d.getFullYear();
  return month >= 9 ? year + 1 : year;
}

// Builds a category x fiscal-year breakdown for year-over-year spend comparison.
export function buildCategoryYearMatrix(expenses: Expense[]): CategoryYearMatrix {
  const yearSet = new Set<number>();
  const byCategory = new Map<ExpenseCategory, Record<number, number>>();

  for (const e of expenses) {
    const year = fiscalYear(e.expense_date);
    yearSet.add(year);
    const catMap = byCategory.get(e.category) ?? {};
    catMap[year] = (catMap[year] ?? 0) + e.amount;
    byCategory.set(e.category, catMap);
  }

  const years = Array.from(yearSet).sort((a, b) => b - a);

  const rows: CategoryYearRow[] = Array.from(byCategory.entries())
    .map(([category, byYear]) => ({
      category,
      byYear,
      total: Object.values(byYear).reduce((sum, v) => sum + v, 0),
    }))
    .sort((a, b) => b.total - a.total);

  const yearTotals: Record<number, number> = {};
  for (const year of years) {
    yearTotals[year] = rows.reduce((sum, r) => sum + (r.byYear[year] ?? 0), 0);
  }

  const grandTotal = rows.reduce((sum, r) => sum + r.total, 0);

  return { years, rows, yearTotals, grandTotal };
}
